// HandednessToggle — calm 2-button toggle that picks which edge of
// the cooking pane carries the per-task checkboxes and the vertical
// Look-Back slider. One inline SVG hand glyph drawn once; the right
// button mirrors it with `transform: scaleX(-1)`.
//
// `aria-pressed` flags the active side; both buttons stay visible
// (active filled, inactive outlined) for discoverability.

import { theme } from '../theme';
import type { Handedness } from '../../state/store';

export interface HandednessToggleProps {
  value: Handedness;
  onChange: (h: Handedness) => void;
}

function HandIcon({ side }: { side: Handedness }) {
  // A simple open palm. The path is drawn for a right hand; the left
  // variant just flips horizontally so we only maintain one asset.
  // Stylised to read well at 20px — not anatomically perfect.
  return (
    <svg
      viewBox="0 0 24 24"
      width="20"
      height="20"
      aria-hidden="true"
      style={{
        display: 'block',
        transform: side === 'left' ? 'scaleX(-1)' : 'none',
      }}
    >
      <path
        d="M9 3.5c0-.83.67-1.5 1.5-1.5S12 2.67 12 3.5V11h.5V2.5c0-.83.67-1.5 1.5-1.5s1.5.67 1.5 1.5V11h.5V4.5c0-.83.67-1.5 1.5-1.5s1.5.67 1.5 1.5V11h.5V7c0-.83.67-1.5 1.5-1.5S21 6.17 21 7v8.5c0 3.59-2.91 6.5-6.5 6.5h-1.59c-1.73 0-3.4-.69-4.62-1.91L4.7 16.6c-.59-.59-.59-1.54 0-2.12.59-.59 1.54-.59 2.12 0L9 16.66V3.5z"
        fill="currentColor"
        stroke="currentColor"
        strokeWidth="0.4"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function HandButton({
  side,
  active,
  onClick,
}: {
  side: Handedness;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      aria-label={`${side === 'left' ? 'Left' : 'Right'}-handed layout`}
      data-side={side}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '32px',
        height: '32px',
        padding: 0,
        borderRadius: '8px',
        cursor: 'pointer',
        background: active ? theme.accent.calm : 'transparent',
        color: active ? theme.bg.base : theme.text.muted,
        border: `1px solid ${active ? theme.accent.calm : theme.bg.muted}`,
        transition: `background ${theme.motion.duration.fast} ${theme.motion.easing}, color ${theme.motion.duration.fast} ${theme.motion.easing}`,
      }}
    >
      <HandIcon side={side} />
    </button>
  );
}

export function HandednessToggle({ value, onChange }: HandednessToggleProps) {
  return (
    <div
      role="group"
      aria-label="Handedness"
      style={{
        display: 'inline-flex',
        gap: '0.25rem',
        alignItems: 'center',
      }}
    >
      <HandButton side="left" active={value === 'left'} onClick={() => onChange('left')} />
      <HandButton side="right" active={value === 'right'} onClick={() => onChange('right')} />
    </div>
  );
}

export default HandednessToggle;
